import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money, dateFr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Warning, CheckCircle } from "@phosphor-icons/react";

const FILTERS = [{ v: "open", l: "En cours" }, { v: "resolved", l: "Résolus" }, { v: "all", l: "Tous" }];

export default function Disputes() {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState([]);
  const [dispFor, setDispFor] = useState(null);
  const [dispResp, setDispResp] = useState("");

  const load = () => {
    api.get(`/disputes?status=${filter}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [filter]);

  const openCount = rows.filter((r) => r.dispute?.status === "open").length;

  const resolve = async () => {
    try {
      await api.post(`/expenses/${dispFor.id}/resolve-dispute`, { response: dispResp });
      toast.success("Écart marqué comme résolu");
      setDispFor(null); setDispResp(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="disputes-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Écarts signalés</h1>
        <p className="text-muted-foreground mt-1">Contestations soumises par les fournisseurs depuis leur portail — à traiter d'un coup d'œil.</p>
      </div>

      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button key={f.v} data-testid={`disp-filter-${f.v}`} onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${filter === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"}`}>
            {f.l}{f.v === "open" && openCount > 0 ? ` (${openCount})` : ""}
          </button>
        ))}
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Fournisseur</TableHead>
              <TableHead>Facture</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Signalé le</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucun écart {filter === "open" ? "en cours" : filter === "resolved" ? "résolu" : ""}.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`dispute-row-${r.id}`}>
                <TableCell className="font-medium">{r.vendor}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.description || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.total)}</TableCell>
                <TableCell className="text-sm max-w-xs truncate" title={r.dispute?.message}>{r.dispute?.message}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{dateFr(r.dispute?.created_at)}</TableCell>
                <TableCell>
                  {r.dispute?.status === "resolved" ? (
                    <span className="text-xs px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-200">Résolu</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-sm border bg-red-50 text-red-700 border-red-200">En cours</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`open-dispute-${r.id}`} variant="outline" size="sm" className="rounded-sm" onClick={() => { setDispFor(r); setDispResp(r.dispute?.response || ""); }}>
                    Voir
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dispFor} onOpenChange={(v) => !v && setDispFor(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} /> Écart signalé par le fournisseur</DialogTitle></DialogHeader>
          {dispFor?.dispute && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">{dispFor.vendor} · <span className="tabular-nums text-foreground">{money(dispFor.total)}</span> · {dateFr(dispFor.date)}</div>
              <div className="border border-border rounded-sm p-3 bg-muted/40 text-sm">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Message du fournisseur</div>
                <div className="whitespace-pre-wrap">{dispFor.dispute.message}</div>
                <div className="text-xs text-muted-foreground mt-2">Signalé par {dispFor.dispute.by || "—"} · {dateFr(dispFor.dispute.created_at)}</div>
              </div>
              {dispFor.dispute.status === "resolved" ? (
                <div className="text-sm text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-sm px-3 py-2">
                  Écart résolu{dispFor.dispute.response ? ` — ${dispFor.dispute.response}` : ""}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Réponse / note de résolution (optionnel)</Label>
                  <Textarea data-testid="disputes-response" className="rounded-sm" value={dispResp} onChange={(e) => setDispResp(e.target.value)} placeholder="Ex. : montant corrigé, crédit émis, etc." />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDispFor(null)}>Fermer</Button>
            {dispFor?.dispute?.status !== "resolved" && <Button data-testid="disputes-resolve-btn" className="rounded-sm" onClick={resolve}><CheckCircle size={16} className="mr-1" /> Marquer comme résolu</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
